import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '@/lib/logger';
import { useAuth } from '@/context/AuthContext';
import { useAI } from '@/context/AIContext';
import { useTheme } from '@/context/ThemeContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  LogOut, Settings, Sun, Moon, Upload, Target, Users, BarChart3,
  Search, Building2, Loader2, ListTodo, Activity, Home, Zap, MoreHorizontal, Download, BookUser, FolderOpen, HelpCircle
} from 'lucide-react';
import CommandPalette from '@/components/CommandPalette';
import Onboarding from '@/components/Onboarding';
import LeadsOnboarding from '@/components/LeadsOnboarding';
import LeadsKanban from '@/components/dashboard/LeadsKanban';
import ClientsSection from '@/components/dashboard/ClientsSection';
import AnalyticsDashboard from '@/components/dashboard/AnalyticsDashboard';
import MyTasksDashboard from '@/components/dashboard/MyTasksDashboard';
import ClientHealthScores from '@/components/dashboard/ClientHealthScores';
import FamilyHealthDashboard from '@/components/dashboard/FamilyHealthDashboard';
import NotificationBell from '@/components/dashboard/NotificationBell';
import QuickCapture from '@/components/dashboard/QuickCapture';
import ClientIntakeWizard from '@/components/dashboard/ClientIntakeWizard';
import CustomisableHome from '@/components/dashboard/customiser/CustomisableHome';
import { toast } from 'sonner';

// Tab definitions
const TABS = [
  { id: 'home', label: 'Home', icon: Home, description: "Today's briefing", mobileNav: true },
  { id: 'my-tasks', label: 'Tasks', icon: ListTodo, description: 'Your personal tasks', mobileNav: true },
  { id: 'leads', label: 'Leads', icon: Target, description: 'Manage your pipeline', mobileNav: true },
  { id: 'clients', label: 'Clients', icon: Users, description: 'Family Groups', mobileNav: true },
  { id: 'health', label: 'Health & Analytics', icon: Activity, description: 'Client health and performance', mobileNav: false }
];

const MOBILE_PRIMARY_TABS = TABS.filter(t => t.mobileNav);
const MOBILE_MORE_TABS = TABS.filter(t => !t.mobileNav);

export default function Dashboard() {
  const navigate = useNavigate();
  const { leadsEnabled } = useAI();
  const { user, token, organisation, loading: authLoading, logout, isOrgAdmin } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('home');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showLeadsOnboarding, setShowLeadsOnboarding] = useState(false);
  const [showQuickCapture, setShowQuickCapture] = useState(false);
  const [showClientIntake, setShowClientIntake] = useState(false);
  const [clients, setClients] = useState([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [orgType, setOrgType] = useState('mfo'); // sfo, mfo, advice
  const [sfoRedirecting, setSfoRedirecting] = useState(false);

  // Normalise organisation type to a short code. The DB stores either short
  // codes ('sfo','mfo','advice') or long labels ('Single Family Office', etc).
  const normaliseOrgType = (raw) => {
    if (!raw) return 'mfo';
    const s = String(raw).toLowerCase();
    if (s === 'sfo' || s.includes('single family')) return 'sfo';
    if (s === 'mfo' || s.includes('multi-family') || s.includes('multi family')) return 'mfo';
    if (s === 'advice' || s.includes('advice') || s.includes('planning') || s.includes('boutique')) return 'advice';
    return s;
  };

  // SFOs (Single Family Office) never see leads — single source of truth.
  // For an SFO the Analytics dashboard is also nearly all pipeline-shaped
  // (funnel, win rate, adviser leaderboard, pipeline value), so we don't
  // append it inside the merged Health & Analytics tab for SFOs.
  const showLeads = leadsEnabled && orgType !== 'sfo';
  const showAnalytics = orgType !== 'sfo';
  const visibleTabs = TABS.filter(t => {
    if (t.id === 'leads' && !showLeads) return false;
    return true;
  });
  const MOBILE_PRIMARY = visibleTabs.filter(t => t.mobileNav);
  const MOBILE_MORE = visibleTabs.filter(t => !t.mobileNav);

  const handleExport = async (type) => {
    setShowExportMenu(false);
    try {
      const endpoints = { clients: '/export/clients-list', leads: '/export/leads', tasks: '/export/tasks' };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoints[type]}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.warn('Dashboard: CSV export failed', e);
    }
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/login');
    }
  }, [authLoading, token, navigate]);

  // Fetch organisation type and clients
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const currentOrgType = normaliseOrgType(organisation?.type);
        setOrgType(currentOrgType);

        // Fetch clients for onboarding state and Clients-tab redirect logic
        const response = await api.getClients(token);
        const clientsList = response.data || [];
        setClients(clientsList);

        // SFOs land on My Home like everyone else (no auto-redirect to the
        // family overview). Clicking the Clients tab still shortcuts to the
        // single family's overview — handled by the dedicated effect below.
        if (currentOrgType === 'sfo' && clientsList.length === 0) {
          toast.info('Welcome! Create your first client to get started.');
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
    // normaliseOrgType is a stable in-component helper — safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organisation, navigate]);

  // Global keyboard shortcut: Cmd/Ctrl+K opens Command Palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // SFO with a single client — skip the clients list, go straight to the overview.
  useEffect(() => {
    if (orgType === 'sfo' && activeTab === 'clients' && clients.length === 1) {
      navigate(`/app/clients/${clients[0].id}/overview`);
    }
  }, [orgType, activeTab, clients, navigate]);

  // Detect demo session — the DemoAccess + DemoPreview handlers set this flag.
  const isDemoSession = (() => {
    try { return sessionStorage.getItem('qindex_demo_session') === '1'; }
    catch { return false; }
  })();

  // Check onboarding status
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!token || sfoRedirecting) return;
      // Demo prospects ALWAYS see the tour (regardless of server-side flag) so
      // every prospect gets the walkthrough on their first landing.
      if (isDemoSession) {
        setTimeout(() => setShowOnboarding(true), 700);
        return;
      }
      try {
        const response = await api.getUserPreferences(token);
        if (response.data?.onboarding_completed === false) {
          setTimeout(() => setShowOnboarding(true), 500);
        }
      } catch (error) {
        logger.warn('Dashboard: onboarding state lookup failed', error);
      }
    };
    checkOnboarding();
  }, [token, sfoRedirecting, isDemoSession]);

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    // Don't pollute the demo-owner's server-side preference — let the next
    // prospect see the tour from scratch.
    if (isDemoSession) return;
    try {
      await api.updateUserPreferences(token, { onboarding_completed: true });
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    setShowOnboarding(false);
    if (isDemoSession) return;
    try {
      await api.updateUserPreferences(token, { onboarding_completed: true });
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  };

  if (authLoading || sfoRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary, #F9F7F5)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p style={{ color: 'var(--text-secondary, #5D524D)' }}>
            {sfoRedirecting ? 'Loading your client dashboard...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300 pb-16 md:pb-0" style={{ backgroundColor: 'var(--bg-primary, #F9F7F5)' }}>
      {/* Desktop Header */}
      <header className="border-b px-4 sm:px-8 py-4 sticky top-0 z-40 transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Logo & Title */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #4A3B32)' }}>
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-heading text-xl" style={{ color: 'var(--text-primary, #2C2420)' }}>
                {organisation?.name || 'QIndex One'}
              </h1>
              <span className="text-xs" style={{ color: 'var(--text-secondary, #5D524D)' }}>Client 360</span>
            </div>
          </div>

          {/* Navigation Tabs — Desktop only */}
          <nav className="hidden md:flex items-center gap-1 bg-[#F5F3F1] rounded-lg p-1" data-testid="dashboard-tabs">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap",
                    isActive 
                      ? "bg-white text-[#2C2420] shadow-sm" 
                      : "text-[#5D524D] hover:text-[#2C2420] hover:bg-white/50"
                  )}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Mobile: Active tab title */}
          <div className="md:hidden flex-1 text-center">
            <span className="text-sm font-medium text-[#2C2420] capitalize">{TABS.find(t => t.id === activeTab)?.label || 'Home'}</span>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1 sm:gap-3 shrink-0 flex-wrap justify-end">
            <NotificationBell />
            <button
              onClick={() => setShowCommandPalette(true)}
              className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-lg border border-[#E6E0D9] hover:border-[#4A3B32]/30 text-[#8C8078] hover:text-[#2C2420] transition-colors"
              data-testid="cmd-k-trigger"
              title="Quick search"
            >
              <Search className="w-4 h-4" />
              <span className="text-xs hidden lg:inline">Search...</span>
              <kbd className="hidden lg:inline-flex text-[10px] px-1.5 py-0.5 rounded border border-[#E6E0D9] bg-[#F9F7F5] text-[#8C8078] ml-1">
                {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}K
              </kbd>
            </button>
            {['super_admin', 'owner', 'admin', 'org_admin', 'practice_manager'].includes(user?.role) && (
              <Button variant="ghost" size="icon" onClick={() => navigate('/app/business-admin')} className="text-[#5D524D] hover:text-[#2C2420] hidden sm:flex" data-testid="business-admin-btn" title="Business Administration">
                <Users className="w-5 h-5" />
              </Button>
            )}
            <div className="relative hidden sm:block">
              <Button variant="ghost" size="icon" onClick={() => setShowExportMenu(!showExportMenu)} className="text-[#5D524D] hover:text-[#2C2420]" data-testid="export-btn" title="Export Data">
                <Download className="w-5 h-5" />
              </Button>
              {showExportMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-[#E6E0D9] bg-white shadow-lg z-50 py-1" data-testid="export-menu">
                    <button onClick={() => handleExport('clients')} className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0]" data-testid="export-clients">Export Clients (CSV)</button>
                    {showLeads && (
                      <button onClick={() => handleExport('leads')} className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0]" data-testid="export-leads">Export Leads (CSV)</button>
                    )}
                    <button onClick={() => handleExport('tasks')} className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0]" data-testid="export-tasks">Export Tasks (CSV)</button>
                  </div>
                </>
              )}
            </div>
            <div className="h-6 w-px bg-[#E6E0D9] hidden sm:block" />

            {/* User avatar opens a profile menu (not a duplicate Settings shortcut) */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu((v) => !v)}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                data-testid="user-profile-btn"
                aria-haspopup="menu"
                aria-expanded={showProfileMenu}
              >
                <div className="w-8 h-8 rounded-full bg-[#4A3B32] flex items-center justify-center text-white text-sm font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="text-left hidden lg:block">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>{user?.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary, #5D524D)' }}>{user?.role?.replace('_', ' ')}</p>
                </div>
              </button>
              {showProfileMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-[#E6E0D9] bg-white shadow-lg z-50 py-1.5" data-testid="profile-menu">
                    <div className="px-4 py-2 border-b border-[#F0EAE3]">
                      <p className="text-sm font-medium text-[#2C2420] truncate">{user?.name}</p>
                      <p className="text-xs text-[#8C8078] truncate">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate('/app/settings'); }}
                      className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0] flex items-center gap-2"
                      data-testid="profile-menu-settings"
                    >
                      <Settings className="w-4 h-4 text-[#5D524D]" /> Account settings
                    </button>
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate('/app/adviser-hub'); }}
                      className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0] flex items-center gap-2"
                      data-testid="profile-menu-adviser-hub"
                    >
                      <BookUser className="w-4 h-4 text-[#5D524D]" /> Internal Adviser Hub
                    </button>
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate('/app/library'); }}
                      className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0] flex items-center gap-2"
                      data-testid="profile-menu-library"
                    >
                      <FolderOpen className="w-4 h-4 text-[#5D524D]" /> Document Library
                    </button>
                    <button
                      onClick={() => { setShowProfileMenu(false); toggleTheme(); }}
                      className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0] flex items-center gap-2"
                      data-testid="profile-menu-theme"
                    >
                      {isDark ? <Sun className="w-4 h-4 text-[#5D524D]" /> : <Moon className="w-4 h-4 text-[#5D524D]" />}
                      {isDark ? 'Switch to light' : 'Switch to dark'}
                    </button>
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate('/app/support'); }}
                      className="w-full text-left px-4 py-2 text-sm text-[#2C2420] hover:bg-[#F5F3F0] flex items-center gap-2"
                      data-testid="profile-menu-support"
                    >
                      <HelpCircle className="w-4 h-4 text-[#5D524D]" /> Help & support
                    </button>
                    <div className="border-t border-[#F0EAE3] my-1" />
                    <button
                      onClick={() => { setShowProfileMenu(false); logout(); window.location.href = '/login'; }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid="profile-menu-logout"
                    >
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E6E0D9] px-2 py-1 safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around">
          {MOBILE_PRIMARY.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setShowMoreMenu(false); }}
                className={cn(
                  "flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all",
                  isActive ? "text-[#4A3B32]" : "text-[#8C8078]"
                )}
                data-testid={`mobile-tab-${tab.id}`}
              >
                <Icon className={cn("w-5 h-5", isActive && "text-[#4A3B32]")} />
                <span className={cn("text-[10px] leading-tight", isActive ? "font-semibold" : "font-normal")}>{tab.label}</span>
                {isActive && <div className="w-4 h-0.5 bg-[#4A3B32] rounded-full mt-0.5" />}
              </button>
            );
          })}
          {/* More button */}
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className={cn(
              "flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all relative",
              (showMoreMenu || MOBILE_MORE.some(t => t.id === activeTab)) ? "text-[#4A3B32]" : "text-[#8C8078]"
            )}
            data-testid="mobile-tab-more"
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[10px] leading-tight font-normal">More</span>
          </button>
        </div>

        {/* More menu overlay */}
        {showMoreMenu && (
          <div className="absolute bottom-full left-0 right-0 bg-white border-t border-[#E6E0D9] shadow-lg rounded-t-xl px-4 py-3 mb-0" data-testid="more-menu">
            <div className="grid grid-cols-3 gap-2">
              {MOBILE_MORE.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setShowMoreMenu(false); }}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all",
                      isActive ? "bg-[#4A3B32] text-white" : "bg-[#F5F3F0] text-[#5D524D] hover:bg-[#E6E0D9]"
                    )}
                    data-testid={`more-tab-${tab.id}`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium">{tab.label}</span>
                  </button>
                );
              })}
              <button
                onClick={() => { navigate('/app/settings'); setShowMoreMenu(false); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#F5F3F0] text-[#5D524D] hover:bg-[#E6E0D9] transition-all"
                data-testid="more-tab-settings"
              >
                <Settings className="w-6 h-6" />
                <span className="text-xs font-medium">Settings</span>
              </button>
              {['super_admin', 'owner', 'admin', 'org_admin', 'practice_manager'].includes(user?.role) && (
                <button
                  onClick={() => { navigate('/app/business-admin'); setShowMoreMenu(false); }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#F5F3F0] text-[#5D524D] hover:bg-[#E6E0D9] transition-all"
                  data-testid="more-tab-business-admin"
                >
                  <Users className="w-6 h-6" />
                  <span className="text-xs font-medium">Admin</span>
                </button>
              )}
              <button
                onClick={() => { navigate('/app/adviser-hub'); setShowMoreMenu(false); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#F5F3F0] text-[#5D524D] hover:bg-[#E6E0D9] transition-all"
                data-testid="more-tab-adviser-hub"
              >
                <BookUser className="w-6 h-6" />
                <span className="text-xs font-medium">Advisers</span>
              </button>
              <button
                onClick={() => { navigate('/app/library'); setShowMoreMenu(false); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#F5F3F0] text-[#5D524D] hover:bg-[#E6E0D9] transition-all"
                data-testid="more-tab-library"
              >
                <FolderOpen className="w-6 h-6" />
                <span className="text-xs font-medium">Library</span>
              </button>
              <button
                onClick={() => { navigate('/app/support'); setShowMoreMenu(false); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#F5F3F0] text-[#5D524D] hover:bg-[#E6E0D9] transition-all"
                data-testid="more-tab-support"
              >
                <HelpCircle className="w-6 h-6" />
                <span className="text-xs font-medium">Support</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        {/* Tab Content */}
        {activeTab === 'home' && (
          <CustomisableHome
            token={token}
            ctx={{
              setActiveTab,
              setShowQuickCapture,
              setShowClientIntake,
              navigate,
              showLeads,
              orgType,
            }}
          />
        )}
        {activeTab === 'leads' && <LeadsKanban onStartTour={() => setShowLeadsOnboarding(true)} />}
        {activeTab === 'clients' && <ClientsSection onClientIntake={() => setShowClientIntake(true)} />}
        {activeTab === 'my-tasks' && <MyTasksDashboard onQuickCapture={() => setShowQuickCapture(true)} />}
        {activeTab === 'health' && (
          <div className="space-y-10" data-testid="health-and-analytics-tab">
            {orgType === 'sfo'
              ? <FamilyHealthDashboard />
              : <ClientHealthScores onClientClick={(id) => navigate(`/app/clients/${id}/overview`)} />}
            {showAnalytics && (
              <section data-testid="analytics-section">
                <div className="flex items-center gap-3 mb-3">
                  <BarChart3 className="w-5 h-5 text-[#4A3B32]" />
                  <h2 className="font-heading text-xl text-[#2C2420]">Performance analytics</h2>
                </div>
                <AnalyticsDashboard />
              </section>
            )}
          </div>
        )}
      </main>

      {/* Onboarding Tour */}
      <Onboarding
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
        clients={clients}
      />

      {/* Leads Onboarding Tour */}
      <LeadsOnboarding
        isOpen={showLeadsOnboarding}
        onComplete={() => setShowLeadsOnboarding(false)}
        onSkip={() => setShowLeadsOnboarding(false)}
      />

      {/* Quick Capture Modal */}
      <QuickCapture
        isOpen={showQuickCapture}
        onClose={() => setShowQuickCapture(false)}
        onTaskCreated={() => {}}
      />

      {/* Client Intake Wizard */}
      <ClientIntakeWizard
        isOpen={showClientIntake}
        onClose={() => setShowClientIntake(false)}
        onComplete={(clientId) => navigate(`/app/clients/${clientId}/overview`)}
      />

      {/* Command Palette (Cmd+K) */}
      <CommandPalette
        open={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onTabChange={(tab) => setActiveTab(tab)}
      />
    </div>
  );
}
